<script setup>
import { defineProps, defineEmits } from 'vue'

const emit = defineEmits(['value'])

const props = defineProps({
  name: String,
  description: String,
  required: Boolean,
  options: Array,      // Array of {value: string, label: string}
  value: Array         // Selected values
})

function toggleOption(optionValue) {
  const newValue = [...props.value]
  const index = newValue.indexOf(optionValue)

  if (index > -1) {
    newValue.splice(index, 1)
  } else {
    newValue.push(optionValue)
  }

  emit('value', newValue)
}

function isSelected(optionValue) {
  return props.value.includes(optionValue)
}
</script>

<template>
  <div>
    <div class="param-container">
      <div class="param-header">
        <span class="me-2 param-name">{{ props.name }}</span>
        <span class="type-badge">array</span>
        <span v-if="props.required" class="ms-2 required-badge">required</span>
      </div>
      <p class="param-description">{{ props.description }}</p>

      <div class="options-grid">
        <div
          v-for="option in props.options"
          :key="option.value"
          class="option-item"
        >
          <label class="checkbox-label">
            <input
              type="checkbox"
              :checked="isSelected(option.value)"
              @change="toggleOption(option.value)"
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="option-label">{{ option.label }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.param-container {
  padding: 1rem;
  border-radius: var(--radius-lg);
  background-color: var(--surface-sunken);
}

.param-header {
  display: flex;
  align-items: center;
  font-weight: normal;
  margin-bottom: 0.5rem;
}

.param-name {
  color: var(--text-primary);
  font-weight: 600;
  font-family: var(--font-heading);
  font-size: 0.95rem;
}

.type-badge {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--accent-text);
  background: var(--accent-soft-2);
  padding: 0.1rem 0.5rem;
  border-radius: var(--radius-xs);
}

.required-badge {
  font-size: var(--fs-xs);
  font-weight: 500;
  color: var(--color-error-text);
  background: var(--color-error-soft);
  padding: 0.1rem 0.5rem;
  border-radius: var(--radius-xs);
}

.param-description {
  color: var(--text-secondary);
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

.option-item {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0;
  user-select: none;
  width: 100%;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkbox-custom {
  position: relative;
  width: 18px;
  height: 18px;
  border: 2px solid var(--border);
  border-radius: var(--radius-sm);
  margin-right: 0.5rem;
  transition: var(--transition);
  flex-shrink: 0;
}

.checkbox-input:checked ~ .checkbox-custom {
  background-color: var(--accent-text);
  border-color: var(--accent-text);
}

.checkbox-input:checked ~ .checkbox-custom::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-label:hover .checkbox-custom {
  border-color: var(--accent-text);
}

.option-label {
  color: var(--text-primary);
  font-size: 0.9rem;
}
</style>
